// @ts-strict-ignore
import { FetchResult } from "@apollo/client";
import { useUser } from "@dashboard/auth";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { CardSpacer } from "@dashboard/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import { useDevModeContext } from "@dashboard/components/DevModePanel/hooks";
import Form from "@dashboard/components/Form";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Metadata, MetadataIdSchema } from "@dashboard/components/Metadata";
import { Savebar } from "@dashboard/components/Savebar";
import { AppWidgets } from "@dashboard/extensions/components/AppWidgets/AppWidgets";
import { extensionMountPoints } from "@dashboard/extensions/extensionMountPoints";
import { getExtensionsItemsForOrderDetails } from "@dashboard/extensions/getExtensionsItems";
import { useExtensions } from "@dashboard/extensions/hooks/useExtensions";
import {
  OrderDetailsFragment,
  OrderDetailsQuery,
  OrderErrorFragment,
  OrderNoteUpdateMutation,
  OrderStatus,
  TransactionActionEnum,
} from "@dashboard/graphql";
import { useBackLinkWithState } from "@dashboard/hooks/useBackLinkWithState";
import { SubmitPromise } from "@dashboard/hooks/useForm";
import useNavigator from "@dashboard/hooks/useNavigator";
import { defaultGraphiQLQuery } from "@dashboard/orders/queries";
import { orderListUrl } from "@dashboard/orders/urls";
import { Button, Divider } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { useIntl } from "react-intl";

import { getMutationErrors, maybe } from "../../../misc";
import OrderChannelSectionCard from "../OrderChannelSectionCard";
import OrderCustomer from "../OrderCustomer";
import OrderCustomerNote from "../OrderCustomerNote";
import OrderDraftDetails from "../OrderDraftDetails/OrderDraftDetails";
import { FormData as OrderDraftDetailsProductsFormData } from "../OrderDraftDetailsProducts";
import OrderFulfilledProductsCard from "../OrderFulfilledProductsCard";
import OrderHistory, { FormData as HistoryFormData } from "../OrderHistory";
import OrderInvoiceList from "../OrderInvoiceList";
import { OrderLineDiscountDialog } from "../OrderLineDiscountDialog/OrderLineDiscountDialog";
import { OrderPaymentOrTransaction } from "../OrderPaymentOrTransaction/OrderPaymentOrTransaction";
import OrderPlacedDiscountCard from "../OrderPlacedDiscountCard/OrderPlacedDiscountCard";
import { orderPrintMessages } from "../OrderPrintDialog/messages";
import { OrderPrintDialog } from "../OrderPrintDialog/OrderPrintDialog";
import { getOrderPrintDocument } from "../OrderPrintDialog/utils";
import { OrderProgressStepper } from "../OrderProgressStepper";
import { getActiveStepIndex, getOrderProgressSteps } from "../OrderProgressStepper/utils";
import OrderUnfulfilledProductsCard from "../OrderUnfulfilledProductsCard";
import { messages } from "./messages";
import Title from "./Title";
import {
  createMetadataHandler,
  createOrderMetadataIdSchema,
  filteredConditionalItems,
  hasAnyItemsReplaceable,
} from "./utils";

interface OrderDetailsPageProps {
  order: OrderDetailsFragment | OrderDetailsFragment;
  shop: OrderDetailsQuery["shop"];
  shippingMethods?: Array<{
    id: string;
    name: string;
  }>;
  loading: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  errors: OrderErrorFragment[];
  onOrderLineAdd?: () => void;
  onOrderLineChange?: (id: string, data: OrderDraftDetailsProductsFormData) => void;
  onOrderLineRemove?: (id: string) => void;
  onShippingMethodEdit?: () => void;
  onBillingAddressEdit: () => any;
  onFulfillmentApprove: (id: string) => any;
  onFulfillmentCancel: (id: string) => any;
  onShowMetadata: (id: string) => void;
  onFulfillmentTrackingNumberUpdate: (id: string) => any;
  onOrderFulfill: () => any;
  onProductClick?: (id: string) => any;
  onPaymentCapture: () => any;
  onMarkAsPaid: () => any;
  onConfirm?: () => void;
  onPaymentRefund: () => any;
  onPaymentVoid: () => any;
  onShippingAddressEdit: () => any;
  onOrderCancel: () => any;
  onNoteAdd: (data: HistoryFormData) => any;
  onNoteUpdate: (id: string, message: string) => Promise<FetchResult<OrderNoteUpdateMutation>>;
  onNoteUpdateLoading: boolean;
  onProfileView: () => any;
  onOrderReturn: () => any;
  onInvoiceClick: (invoiceId: string) => any;
  onInvoiceGenerate: () => any;
  onInvoiceMarkToggle: (marked: boolean) => any;
  onReceivedMarkToggle: (marked: boolean) => any;
  onPickupReadyMarkToggle?: (marked: boolean) => any;
  onResendInvoice: () => any;
  onResendShipping: () => any;
  onResendConfirmation: () => any;
  onResendPayment: () => any;
  onSendPaymentReminder: () => any;
  onInvoiceSend: (invoiceId: string) => any;
  onTransactionAction: (transactionId: string, actionType: TransactionActionEnum) => any;
  onAddManualTransaction: () => any;
  onRefundAdd: () => void;
  onSubmit: (data: MetadataIdSchema) => SubmitPromise;
}

const OrderDetailsPage = (props: OrderDetailsPageProps) => {
  const {
    loading,
    order,
    shop,
    saveButtonBarState,
    errors,
    onBillingAddressEdit,
    onFulfillmentApprove,
    onFulfillmentCancel,
    onFulfillmentTrackingNumberUpdate,
    onNoteAdd,
    onNoteUpdate,
    onNoteUpdateLoading,
    onOrderCancel,
    onOrderFulfill,
    onPaymentCapture,
    onPaymentRefund,
    onPaymentVoid,
    onShippingAddressEdit,
    onProfileView,
    onInvoiceClick,
    onInvoiceGenerate,
    onInvoiceMarkToggle,
    onReceivedMarkToggle,
    onPickupReadyMarkToggle,
    onResendInvoice,
    onResendShipping,
    onResendConfirmation,
    onResendPayment,
    onSendPaymentReminder,
    onInvoiceSend,
    onOrderReturn,
    onOrderLineAdd,
    onOrderLineChange,
    onOrderLineRemove,
    onShippingMethodEdit,
    onTransactionAction,
    onAddManualTransaction,
    onShowMetadata,
    onMarkAsPaid,
    onConfirm,
    onRefundAdd,
    onSubmit,
  } = props;
  const navigate = useNavigator();
  const intl = useIntl();
  const isOrderUnconfirmed = order?.status === OrderStatus.UNCONFIRMED;
  const canCancel = order?.status !== OrderStatus.CANCELED;
  const canEditAddresses = order?.status !== OrderStatus.CANCELED;
  // `orderUpdateShipping` accepts draft/unconfirmed orders and — via a backend
  // extension — customer-submitted (UNFULFILLED) orders that are not fully paid
  // yet, so staff can still correct the shipping method until payment settles.
  // Any other status returns NOT_EDITABLE, so keep the affordance hidden there.
  const isOrderEditable =
    order?.status === OrderStatus.DRAFT || order?.status === OrderStatus.UNCONFIRMED;
  const canEditUnfulfilledShippingMethod =
    order?.status === OrderStatus.UNFULFILLED && !order?.isPaid;
  const canEditShippingMethod =
    (isOrderEditable || canEditUnfulfilledShippingMethod) &&
    canEditAddresses &&
    !!onShippingMethodEdit;
  const notAllowedToFulfillUnpaid =
    shop?.fulfillmentAutoApprove && !shop?.fulfillmentAllowUnpaid && !order?.isPaid;
  const unfulfilled = (order?.lines || []).filter(line => line.quantityToFulfill > 0);
  // Superusers may add, change and remove items of placed orders that are
  // still in progress (not canceled/expired, and not yet completed or
  // returned); the backend recalculates totals and reallocates stock.
  // Fulfilled quantities stay locked.
  const { user } = useUser();
  const canEditPlacedOrderLines =
    !!user?.isSuperuser &&
    !!order &&
    ![
      OrderStatus.DRAFT,
      OrderStatus.UNCONFIRMED,
      OrderStatus.CANCELED,
      OrderStatus.EXPIRED,
      OrderStatus.FULFILLED,
      OrderStatus.PARTIALLY_RETURNED,
      OrderStatus.RETURNED,
    ].includes(order.status);
  // Staff can set a manual order discount (percentage or fixed) on any placed
  // order except canceled/expired ones; unconfirmed orders use the draft summary.
  const canDiscountPlacedOrder =
    !!order &&
    ![
      OrderStatus.DRAFT,
      OrderStatus.UNCONFIRMED,
      OrderStatus.CANCELED,
      OrderStatus.EXPIRED,
    ].includes(order.status);
  // The note a customer leaves at checkout may arrive either through Saleor's
  // native `customerNote` field or as an `orderNotes` metadata entry (custom
  // checkout). Surface whichever is present as a highlighted section.
  const orderNote =
    order?.metadata?.find(metadataItem => metadataItem.key === "orderNotes")?.value ||
    maybe(() => order.customerNote);
  const handleSubmit = async (data: MetadataIdSchema) => {
    const result = await onSubmit(data);

    return getMutationErrors(result);
  };
  const initial = createOrderMetadataIdSchema(order);
  const saveLabel = isOrderUnconfirmed
    ? { confirm: intl.formatMessage(messages.confirmOrder) }
    : undefined;
  const allowSave = () => {
    if (!isOrderUnconfirmed) {
      return loading;
    } else if (!order?.lines?.length) {
      return true;
    }

    return loading;
  };
  const selectCardMenuItems = filteredConditionalItems([
    {
      item: {
        label: intl.formatMessage(messages.editShippingMethod),
        onSelect: onShippingMethodEdit,
      },
      shouldExist: canEditShippingMethod,
    },
    {
      item: {
        label: intl.formatMessage(messages.cancelOrder),
        onSelect: onOrderCancel,
      },
      shouldExist: canCancel,
    },
    {
      item: {
        label: intl.formatMessage(messages.returnOrder),
        onSelect: onOrderReturn,
      },
      shouldExist: hasAnyItemsReplaceable(order),
    },
  ]);
  const { ORDER_DETAILS_MORE_ACTIONS, ORDER_DETAILS_WIDGETS } = useExtensions(
    extensionMountPoints.ORDER_DETAILS,
  );
  const extensionMenuItems = getExtensionsItemsForOrderDetails(
    ORDER_DETAILS_MORE_ACTIONS,
    order?.id,
  );
  const context = useDevModeContext();
  const openPlaygroundURL = () => {
    context.setDevModeContent(defaultGraphiQLQuery);
    context.setVariables(`{ "id": "${order?.id}" }`);
    context.setDevModeVisibility(true);
  };

  const backLinkUrl = useBackLinkWithState({
    path: orderListUrl(),
  });
  // Pickup ticket for in-store pickup; the courier shipping label only while the
  // order is being packed (the "Preparation" step), when the parcel is labelled.
  const orderPrintDocument =
    order && order.status !== OrderStatus.CANCELED ? getOrderPrintDocument(order) : null;
  const progressSteps = getOrderProgressSteps(order);
  const isPreparationStep = progressSteps[getActiveStepIndex(progressSteps)]?.key === "preparation";
  const printDocument =
    orderPrintDocument === "shippingLabel" && !isPreparationStep ? null : orderPrintDocument;
  const [isPrintDialogOpen, setPrintDialogOpen] = useState(false);

  return (
    <Form confirmLeave initial={initial} onSubmit={handleSubmit} mergeData={false}>
      {({ set, triggerChange, data, submit }) => {
        const handleChangeMetadata = createMetadataHandler(data, set, triggerChange);

        return (
          <DetailPageLayout>
            <TopNav href={backLinkUrl} title={<Title order={order} />}>
              {printDocument && (
                <Button
                  variant="secondary"
                  marginRight={3}
                  onClick={() => setPrintDialogOpen(true)}
                  data-test-id="order-print-button"
                >
                  {intl.formatMessage(
                    printDocument === "pickupTicket"
                      ? orderPrintMessages.printPickupTicketButton
                      : orderPrintMessages.printShippingLabelButton,
                  )}
                </Button>
              )}
              <TopNav.Menu
                dataTestId="menu"
                items={[
                  ...selectCardMenuItems,
                  ...extensionMenuItems,
                  {
                    label: intl.formatMessage(messages.openGraphiQL),
                    onSelect: openPlaygroundURL,
                    testId: "graphiql-redirect",
                  },
                ]}
              />
            </TopNav>

            <DetailPageLayout.Content data-test-id="order-fulfillment">
              <OrderProgressStepper
                order={order}
                onConfirm={onConfirm}
                onMarkAsPaid={onMarkAsPaid}
                onInvoiceGenerate={onInvoiceGenerate}
                onInvoiceMarkToggle={onInvoiceMarkToggle}
                onReceivedMarkToggle={onReceivedMarkToggle}
                onPickupReadyMarkToggle={onPickupReadyMarkToggle}
                onResendInvoice={onResendInvoice}
                onResendShipping={onResendShipping}
                onResendConfirmation={onResendConfirmation}
                onResendPayment={onResendPayment}
                onSendPaymentReminder={onSendPaymentReminder}
                onOrderFulfill={onOrderFulfill}
                onTrackingAdd={onFulfillmentTrackingNumberUpdate}
                onApprove={onFulfillmentApprove}
                notAllowedToFulfillUnpaid={notAllowedToFulfillUnpaid}
              />
              <CardSpacer />
              {!isOrderUnconfirmed ? (
                <OrderUnfulfilledProductsCard
                  lines={unfulfilled}
                  loading={loading}
                  onShowMetadata={onShowMetadata}
                  canEditLineDiscount={canDiscountPlacedOrder}
                  editing={
                    canEditPlacedOrderLines
                      ? {
                          errors,
                          onOrderLineAdd,
                          onOrderLineChange,
                          onOrderLineRemove,
                        }
                      : undefined
                  }
                />
              ) : (
                <>
                  <OrderDraftDetails
                    order={order}
                    errors={errors}
                    loading={loading}
                    onShowMetadata={onShowMetadata}
                    onOrderLineAdd={onOrderLineAdd}
                    onOrderLineChange={onOrderLineChange}
                    onOrderLineRemove={onOrderLineRemove}
                    onShippingMethodEdit={onShippingMethodEdit}
                  />
                  <CardSpacer />
                </>
              )}
              {order?.fulfillments?.map(fulfillment => (
                <OrderFulfilledProductsCard
                  dataTestId="fulfilled-order-section"
                  key={fulfillment.id}
                  fulfillment={fulfillment}
                  order={order}
                  onShowMetadata={onShowMetadata}
                  canEditLineDiscount={canDiscountPlacedOrder}
                  onOrderFulfillmentCancel={() => onFulfillmentCancel(fulfillment.id)}
                  onTrackingCodeAdd={() => onFulfillmentTrackingNumberUpdate(fulfillment.id)}
                >
                  <Metadata
                    isLoading={loading}
                    data={data[fulfillment.id]}
                    onChange={x => handleChangeMetadata(x, fulfillment.id)}
                  />
                </OrderFulfilledProductsCard>
              ))}
              <OrderLineDiscountDialog lines={order?.lines ?? []} />
              {orderNote && <OrderCustomerNote note={orderNote} highlighted />}
              <OrderPaymentOrTransaction
                order={order}
                shop={shop}
                onTransactionAction={onTransactionAction}
                onPaymentCapture={onPaymentCapture}
                onPaymentVoid={onPaymentVoid}
                onPaymentRefund={onPaymentRefund}
                onAddManualTransaction={onAddManualTransaction}
                onRefundAdd={onRefundAdd}
                onShippingMethodEdit={canEditShippingMethod ? onShippingMethodEdit : undefined}
              />
              <Metadata
                isLoading={loading}
                data={data[order?.id]}
                onChange={x => handleChangeMetadata(x, order?.id)}
              />
              <OrderHistory
                history={order?.events}
                onNoteUpdateLoading={onNoteUpdateLoading}
                orderCurrency={order?.total?.gross.currency}
                onNoteAdd={onNoteAdd}
                onNoteUpdate={onNoteUpdate}
              />
            </DetailPageLayout.Content>
            <DetailPageLayout.RightSidebar>
              <OrderCustomer
                canEditAddresses={canEditAddresses}
                canEditCustomer={false}
                order={order}
                errors={errors}
                onBillingAddressEdit={onBillingAddressEdit}
                onShippingAddressEdit={onShippingAddressEdit}
                onProfileView={onProfileView}
              />
              <CardSpacer />
              <OrderChannelSectionCard channel={order?.channel} />
              <CardSpacer />
              {canDiscountPlacedOrder && (
                <>
                  <OrderPlacedDiscountCard />
                  <CardSpacer />
                </>
              )}
              {!isOrderUnconfirmed && (
                <>
                  <OrderInvoiceList
                    invoices={order?.invoices}
                    onInvoiceClick={onInvoiceClick}
                    onInvoiceSend={onInvoiceSend}
                  />
                  <CardSpacer />
                </>
              )}
              {ORDER_DETAILS_WIDGETS.length > 0 && order?.id && (
                <>
                  <CardSpacer />
                  <Divider />
                  <AppWidgets extensions={ORDER_DETAILS_WIDGETS} params={{ orderId: order.id }} />
                </>
              )}
            </DetailPageLayout.RightSidebar>
            <Savebar>
              <Savebar.Spacer />
              <Savebar.CancelButton onClick={() => navigate(orderListUrl())} />
              <Savebar.ConfirmButton
                transitionState={saveButtonBarState}
                onClick={submit}
                disabled={allowSave()}
              >
                {saveLabel?.confirm}
              </Savebar.ConfirmButton>
            </Savebar>
            {printDocument && (
              <OrderPrintDialog
                open={isPrintDialogOpen}
                onClose={() => setPrintDialogOpen(false)}
                order={order}
                document={printDocument}
              />
            )}
          </DetailPageLayout>
        );
      }}
    </Form>
  );
};

OrderDetailsPage.displayName = "OrderDetailsPage";
export default OrderDetailsPage;
